import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Shield, FileCheck, Award, DollarSign, TrendingUp, CheckCircle, Building2, Tractor, Home as HomeIcon, Factory, FileText, Scale } from 'lucide-react';
import { companyInfo, services, whyChooseUs, banks, heroImages } from '../data/mock';
import HeroSlideshow from '../components/HeroSlideshow';
import SEO from '@/components/SEO';

const Home = () => {
  const iconMap = {
    Target, Shield, FileCheck, Award, DollarSign, TrendingUp,
    Building2, Tractor, Home: HomeIcon, Factory, FileText, Scale
  };

  // Map bank display names to filenames placed in public/banklogos/
  const bankLogoMap = {
    'Bhavnagar Nagrik Sahkari Bank': 'bhavanagrNagrikBank.png',
    'Ceejay Finance Ltd.': 'cejay.png',
    'Gujarat Gramin Bank (Old name: Saurashtra Gramin Bank)': 'gujratgraminbank.png',
    'Sihor Mercantile Co-Op Bank Ltd.': 'sihorbank.png',
    'Sihor Nagrik Sahkari Bank Ltd.': 'sihornagrikbank.png',
    'State Bank of India': 'sbi.png',
    'Citizen Co-Op Bank Ltd.': 'citizensbankrajkot.png',
    'Indian Bank': 'indianbank.png',
    'UCO Bank': 'ucobank.png',
    'Bank of Baroda': 'bankofbaroda.png',
    'Punjab National Bank (Gujarat)': 'pnb.png',
    'Punjab National Bank (Maharashtra)': 'pnb.png',
    'Federal Bank': 'federalbank.png',
    'Karnataka Bank': 'karnatakabank.png',
    'Indian Overseas Bank': 'indiaoverseasbank.png',
    'Union Bank of India': 'unionbank.png',
  };

  const BankItem = ({ name }) => {
    const filename = bankLogoMap[name];
    const src = filename ? `/banklogos/${filename}` : null;
    return (
      <div className="bg-card p-4 border border-border hover:shadow-sm transition-shadow flex items-center gap-4">
        {src ? (
          <img src={src} alt={`${name} logo`} className="w-12 h-12 object-contain flex-shrink-0" loading="lazy" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-sm font-semibold text-foreground">{name.split(' ').slice(0,2).map(s=>s[0]).join('').toUpperCase()}</div>
        )}
        <div className="text-sm text-foreground font-medium">{name}</div>
      </div>
    );
  };

  return (
    <div className="bg-background">
      <SEO
        title="Mehta Associates | Property Valuation in Bhavnagar"
        description="Trusted property valuation services in Bhavnagar. Mehta Associates provides certified, government-approved valuation reports for banks and individuals."
        pathname="/"
        keywords={["Property Valuation in Bhavnagar", "Government Approved Valuer", "Mehta Associates"]}
        image={`${process.env.PUBLIC_URL}/office/office-hero.jpg`}
      />
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          {/* Slideshow replacing hero video - uses images from public/office */}
          <div className="hero-video w-full h-full">
            {/* lazy import to avoid SSR issues */}
            <HeroSlideshow images={[
              "WhatsApp Image 2026-03-11 at 6.03.54 PM (1).jpeg",
              "WhatsApp Image 2026-03-11 at 6.03.54 PM.jpeg",
              "WhatsApp Image 2026-03-11 at 6.03.55 PM (1).jpeg",
              "WhatsApp Image 2026-03-11 at 6.03.55 PM (2).jpeg",
              "WhatsApp Image 2026-03-11 at 6.03.55 PM.jpeg",
            ]} interval={5000} />
          </div>
          <div className="hero-overlay"></div>
        </div>
        <div className="container mx-auto px-4">
          <div className="hero-content">
            <h1 className="hero-title">
              Property Valuation in Bhavnagar
            </h1>
            <p className="body-large text-muted mb-4 max-w-2xl">
              Mehta Associates — Government Approved Valuer with {companyInfo.experience} of experience across Gujarat.
            </p>
            <p className="body-medium text-muted max-w-2xl mb-8">
              Trusted property valuation services in Bhavnagar: certified valuation reports accepted by banks and legal authorities.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="btn-primary">
                Get Consultation
              </Link>
              <Link to="/services" className="btn-secondary">
                Explore Services
              </Link>
            </div>
            <div className="mt-12 flex flex-wrap gap-8">
              <div>
                <div className="text-4xl font-bold text-foreground">{companyInfo.experience}</div>
                <div className="text-sm text-[#666666] uppercase">Experience</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-foreground">15+</div>
                <div className="text-sm text-[#666666] uppercase">Banks</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-foreground">24 Hrs</div>
                <div className="text-sm text-[#666666] uppercase">Report Delivery</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="heading-2 text-foreground mb-4">WHY CHOOSE US</h2>
            <p className="body-medium text-muted max-w-2xl mx-auto">
              Certified, accurate, and legally compliant valuation reports for individuals, banks, corporates, and government bodies.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {whyChooseUs.map((item) => {
                const Icon = iconMap[item.icon] || ArrowRight;
              return (
                <div
                  key={item.id}
                  className="bg-card p-8 border border-border hover:shadow-md transition-all duration-300 group"
                >
                  <div className="w-12 h-12 bg-white border border-border rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="text-foreground" size={24} />
                  </div>
                  <h3 className="heading-5 text-foreground mb-3">{item.title}</h3>
                  <p className="body-small text-muted">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-card">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="heading-2 text-foreground mb-4">OUR SERVICES</h2>
            <p className="body-medium text-muted max-w-2xl mx-auto">
              Comprehensive valuation services for all types of properties
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {services.slice(0, 6).map((service) => {
              const Icon = iconMap[service.icon] || ArrowRight;
              return (
                <div
                  key={service.id}
                  className="bg-card p-6 border border-border hover:border-primary transition-all duration-300 group"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-white border border-border rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Icon className="text-foreground" size={20} />
                    </div>
                    <div>
                      <h3 className="heading-6 text-foreground mb-2">{service.title}</h3>
                      <p className="body-small text-muted">{service.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-center">
            <Link to="/services" className="btn-primary inline-flex items-center gap-2">
              View All Services
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Bank Partners */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="heading-2 text-foreground mb-4">RECOGNIZED & EMPANELLED</h2>
            <p className="body-medium text-muted">
              Approved by major banks and financial institutions
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {banks.slice(0, 15).map((bank, index) => (
              <BankItem key={index} name={bank} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
{/* CTA Section */}
<section className="py-20 bg-white">
  <div className="container mx-auto px-4 text-center">
    
    <h2 className="text-3xl md:text-4xl font-bold text-[#0A1A2F] mb-6">
      GET YOUR VALUATION REPORT IN 24 HOURS
    </h2>

    <p className="text-lg text-[#0A1A2F]/80 mb-8 max-w-2xl mx-auto">
      Fast, accurate, and legally compliant property valuation reports delivered the same day
    </p>

    <div className="flex flex-col sm:flex-row gap-4 justify-center">
      
      <Link
        to="/contact"
        className="px-6 py-3 bg-[#0A1A2F] text-white font-semibold rounded-lg hover:bg-[#132a4a] transition duration-300"
      >
        Request Valuation
      </Link>

      <Link
        to="/process"
        className="px-6 py-3 border border-[#0A1A2F] text-[#0A1A2F] rounded-lg hover:bg-[#0A1A2F] hover:text-white transition duration-300"
      >
        Learn Process
      </Link>

    </div>
  </div>
</section>

    </div>
  );
};

export default Home;
